import { addBreadcrumb, captureException, captureMessage } from '@sentry/core';
import { isObject } from 'lodash-es';

import { AclosetMessageError, isAclosetError } from '../AclosetError';
import { isAclosetServiceError } from '../AclosetError/AclosetServiceError';
import { sentryConfig } from './config';

let currentErrorCount = 0;

const captureCurrentErrors = () => {
  if (currentErrorCount >= sentryConfig.maxErrorCount) {
    currentErrorCount = 0;
    acCaptureError(`Exceeded maximum number of runtime errors on ${sentryConfig.platformName}`);
  }
};

/**
 * Acloset 디버그(Sentry alias 용).
 */
export function acDebug(message: string, opt?: unknown) {
  addBreadcrumb({
    type: 'info',
    category: sentryConfig.defaultCategory,
    message,
    level: 'debug',
  });
  if (sentryConfig.dev) {
    opt != null ? console.debug(message, opt) : console.debug(message);
  }
}

/**
 * Acloset Info (Sentry alias 용).
 */
export function acInfo(message: string, opt?: unknown) {
  addBreadcrumb({
    type: 'info',
    category: sentryConfig.defaultCategory,
    message,
    level: 'info',
  });
  opt != null ? console.info(message, opt) : console.info(message);
}

/**
 * Acloset 경고(Sentry alias 용).
 */
export function acWarn(message: string, opt?: unknown) {
  currentErrorCount += 1;

  addBreadcrumb({
    category: sentryConfig.defaultCategory,
    message,
    level: 'warning',
  });
  opt != null ? console.warn(message, opt) : console.warn(message);

  captureCurrentErrors();
}

/**
 * Acloset 에러(Sentry alias 용).
 */
export function acError(message: string | Parameters<typeof addBreadcrumb>[0], opt?: unknown) {
  currentErrorCount += 2;

  const input = typeof message === 'string' ? { message: message } : message;

  addBreadcrumb({
    category: sentryConfig.defaultCategory,
    ...input,

    level: 'error',
    data: {
      errorMessage: isAclosetError(opt) ? opt.userMessage : opt instanceof Error ? opt.message : undefined,
      errorCode: isObject(opt) && 'code' in opt ? opt.code : undefined,
      ...input.data,
    },
  });
  opt != null ? console.error(message, opt) : console.error(message);

  captureCurrentErrors();
}

/**
 * Acloset 에러(Sentry alias 용).
 *
 * 에러 수치를 더 높입니다.
 */
export function acErrorHigh(message: Parameters<typeof acError>[0], opt?: unknown) {
  currentErrorCount += 8;
  acError(message, opt);
}

/**
 * Acloset 크리티컬 에러(Sentry alias 용).
 */
export function acFatal(message: string, opt?: unknown) {
  currentErrorCount += 10;

  addBreadcrumb({
    category: sentryConfig.defaultCategory,
    message,
    level: 'fatal',
    data: {
      errorMessage: isAclosetError(opt) ? opt.userMessage : opt instanceof Error ? opt.message : undefined,
    },
  });
  opt != null ? console.error(message, opt) : console.error(message);

  captureCurrentErrors();
}

/**
 * Acloset 심각한 예외 보고.
 *
 * - 발생하지 말아야하는 로직 오류.
 */
export const acCaptureError = (message: string, captureContext?: Parameters<typeof captureException>[1]) => {
  captureMessage(message, {
    ...captureContext,
    level: 'error',
  });
  console.error(message);
};

/**
 * Acloset 심각한 예외 보고 및 error throw.
 *
 * - 발생하지 말아야하는 로직 오류.
 */
export const acThrowCaptureError = (
  message: string,
  captureContext?: Parameters<typeof captureException>[1],
  code?: string,
): never => {
  acCaptureError(message, captureContext);
  throw new AclosetMessageError(message, message, code);
};

/**
 * Acloset 심각한 예외 보고.
 */
export const acCaptureException = (
  message: string,
  exception: unknown,
  captureContext?: Parameters<typeof captureException>[1],
) => {
  const data: Parameters<typeof addBreadcrumb>[0]['data'] = {};
  if (typeof exception === 'object' && exception) {
    if ('code' in exception) {
      data['code'] = exception.code;
    }
    if ('status' in exception) {
      data['status'] = exception.status;
    }
  }

  if (isAclosetServiceError(exception)) {
    data['error_serviceMessage'] = exception.serviceMessage;
    data['error_requestID'] = exception.requestID;
  }

  addBreadcrumb({
    category: sentryConfig.defaultCategory,
    message,
    level: 'fatal',
    data: data,
  });
  if (typeof exception === 'string') {
    captureMessage(exception, captureContext);
  } else {
    captureException(exception, captureContext);
  }
  console.error(message, exception);
};
